import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl, Typography, Box } from '@mui/material';
import { supportedLanguages } from '../i18n';

interface LanguageSwitcherProps {
    variant?: 'header' | 'icon' | 'sidebar' | 'footer';
}

export function LanguageSwitcher({ variant = 'header' }: LanguageSwitcherProps) {
    const { i18n } = useTranslation();

    const headerStyle = {
        color: 'inherit',
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': { py: 1 }
    };

    const sidebarStyle = {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider'
        },
        '& .MuiSelect-select': {
            py: 0.5,
            fontSize: '0.875rem'
        }
    };

    // Вариант для свернутого сайдбара - показываем только код языка
    if (variant === 'icon') {
        const currentLanguageCode = i18n.language.toUpperCase();

        return (
            <FormControl size="small">
                <Select
                    value={i18n.language}
                    onChange={(e) => i18n.changeLanguage(e.target.value)}
                    displayEmpty
                    renderValue={(value) => (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 32,
                            py: 0.25
                        }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.02em',
                                    lineHeight: 1
                                }}
                            >
                                {currentLanguageCode}
                            </Typography>
                        </Box>
                    )}
                    sx={{
                        minWidth: 48,
                        width: 48,
                        height: 32,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'divider',
                            borderRadius: 2
                        },
                        '& .MuiSelect-select': {
                            py: 0.25,
                            px: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 'auto'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main'
                        }
                    }}
                >
                    {Object.entries(supportedLanguages).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {code.toUpperCase()}
                                </Typography>
                                <Typography variant="body2">
                                    {name}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    // Обычные варианты (header и sidebar)
    return (
        <FormControl size="small" sx={{ minWidth: variant === 'sidebar' ? 120 : 100 }}>
            <Select
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                displayEmpty
                sx={variant === 'header' ? headerStyle : sidebarStyle}
            >
                {Object.entries(supportedLanguages).map(([code, name]) => (
                    <MenuItem key={code} value={code}>
                        {name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
